import React, { Component } from 'react';
import {
  Container, Row, Col, Card, ButtonGroup, Button, Alert, Form, Badge, OverlayTrigger, Popover
} from 'react-bootstrap';
import Alias from "components/controls/alias";
import { AgGridReact } from 'ag-grid-react';
import { GoX, GoInfo, GoTrash, GoPencil } from "react-icons/go";
import { MdOutlineExpandMore, MdRefresh } from "react-icons/md";
import Utils from 'context/utils';
import Navigation from 'context/nav';
import Clipboard from "blob_types/blobhub/compute/workflow/controls/common/clipboard";
import ActionCard, { ActionCards } from "blob_types/blobhub/compute/workflow/controls/common/actionCard";
import ConfirmModal from "components/controls/confirmModal";
import ScheduleModal from '../list/create';
import Preferences from "context/preferences";
import { scheduleStateBadgeClass, executionStatusBadgeClass } from "blob_types/blobhub/compute/scheduler/schedules/badgeClass";
import './index.css';

export default class ScheduleView extends Component {
  constructor(props) {
    super(props);
    const scope = { orgId: this.props.blob.org_id, blobId: this.props.blob.id };

    this.state = {
      details: Preferences.get("blob:scheduler.scheduleDetailsPane", scope) ?? true,
      schedule: null,
      executions: null,
      lastExecutionId: null,
      editScheduleModalShow: false,
      deleteConfirmModalShow: false,
      progress: false,
      error: false,
      message: null,
    };
  }

  componentDidMount() {
    this.loadScheduleAndExecutions();
  }

  updateProgress = (progress, error, message) => {
    this.setState({
      progress: progress,
      error: error,
      message: message
    });
  }

  loadScheduleAndExecutions() {
    this.loadExecutions(false);
  }

  loadExecutions(more) {
    this.updateProgress(true, false, "Loading executions...");

    const scheduleId = this.props.match.params.sectionId;
    const body = {
      "command": "list_executions",
      "schedule_id": scheduleId,
      "include_schedule": true
    };
    if ( more ) {
      body["start_execution_id"] = this.state.lastExecutionId;
    }
    this.props.app.api.revisionsIdDataQueryPost(this.props.revision.id, body)
      .then( response => {
        this.updateProgress(false, false, null);

        const schedule = response.data.schedule;
        const executions = response.data.executions;
        const lastExecutionId = response.data.last_execution_id;

        if ( more ) {
          executions.push(...this.state.executions);
        }

        this.setState({
          schedule: schedule || this.state.schedule,
          executions: executions,
          lastExecutionId: lastExecutionId
        });
      }, error => {
        this.updateProgress(false, true, "Failed to fetch executions: " + error.message);
      });
  }

  updateSchedule(schedule) {
    this.updateProgress(true, false, "Updating schedule...");

    const scheduleId = this.props.match.params.sectionId;
    const body = {
      "command": "update_schedule",
      "schedule_id": scheduleId,
      "enabled": schedule.enabled,
      "repeat": schedule.repeat,
      "timezone": schedule.timezone,
      "invocation_target_type": "workflow",
      "invocation_target": schedule.invocationTarget
    };
    if ( "recurring_cron" === schedule.repeat ) {
      body["cron_expression"] = schedule.cronExpression;
    } else {
      body["invocation_time"] = schedule.invocationTime;
    }
    if ( schedule.startDate ) {
      body["start_date"] = schedule.startDate;
    }
    if ( schedule.endDate ) {
      body["end_date"] = schedule.endDate;
    }
    // A blank field omits the key, which is "no change" — an existing alias cannot be cleared,
    // because the server grammar rejects "".
    if ( schedule.alias && schedule.alias.trim() ) {
      body["alias"] = schedule.alias.trim();
    }
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        this.updateProgress(false, false, null);
        this.loadScheduleAndExecutions();
      }, error => {
        this.updateProgress(false, true, "Failed to update schedule: " + error.message);
      });
  }

  deleteSchedule() {
    this.updateProgress(true, false, "Deleting schedule...");

    const scheduleId = this.props.match.params.sectionId;
    const body = {
      "command": "delete_schedule",
      "schedule_id": scheduleId
    };
    this.props.app.api.revisionsIdDataCommandPost(this.props.revision.id, body)
      .then( response => {
        this.updateProgress(false, false, null);
        const orgId = this.props.match.params.orgId;
        const blobId = this.props.match.params.blobId;
        this.props.history.push(`/${orgId}/${blobId}/schedules`);
      }, error => {
        this.updateProgress(false, true, "Failed to delete schedule: " + error.message);
      });
  }

  formatTarget(schedule) {
    if ( !schedule.invocation_target ) {
      return "No target";
    }
    const target = schedule.invocation_target;
    const org = target.org_id || "";
    const blob = target.blob_id || target.blob_alias || "";
    if ( org && blob ) {
      return `${org}/${blob}`;
    }
    return JSON.stringify(target);
  }

  /**
   * The label standing in for the payload in the row. Deliberately a summary of what is there rather
   * than the opening characters of the JSON: a clipped object literal reads as noise, and the first
   * few characters of a payload tell the reader nothing they would act on.
   */
  static summarizeInputData(inputData) {
    if ( Array.isArray(inputData) ) {
      return `${inputData.length} ${1 === inputData.length ? "item" : "items"}`;
    }
    if ( "object" === typeof inputData ) {
      const keys = Object.keys(inputData).length;
      return `${keys} ${1 === keys ? "key" : "keys"}`;
    }
    // Scalars are short enough to be the label themselves.
    return String(inputData);
  }

  /**
   * Input Data is arbitrary user-supplied JSON with no bounded length, so the row carries only a
   * summary pill and the payload lives in a popover. The popover opens on hover, per the product
   * owner's call, and also on keyboard focus so the value stays reachable without a pointer — that
   * is what the tabIndex is for, as a Badge is not focusable on its own.
   *
   * The pill is the teal .title-tag-badge rather than the muted variant: within this very pane
   * .title-tag-badge-muted already carries a meaning (badgeClass.js renders it for a schedule that
   * is not active), so a muted pill here would read as a state it is not describing.
   */
  renderInputData(inputData) {
    if ( undefined === inputData || null === inputData ) {
      return (<span className="text-muted">No input data</span>);
    }

    const popover = (
      <Popover id="schedule-input-data-popover" className="schedule-input-data-popover">
        <Popover.Content>
          <div className="prop-value-column schedule-input-data-payload">
            <code>{JSON.stringify(inputData, null, 2)}</code>
          </div>
        </Popover.Content>
      </Popover>
    );

    return (
      <OverlayTrigger placement="left" trigger={["hover", "focus"]} overlay={popover}>
        <Badge variant="secondary" className="title-tag-badge schedule-input-data" tabIndex={0}>
          {ScheduleView.summarizeInputData(inputData)}
        </Badge>
      </OverlayTrigger>
    );
  }

  debuggerLinkForExecution(executionId) {
    const target = this.state.schedule && this.state.schedule.invocation_target;
    if ( !target || !executionId ) {
      return null;
    }
    const orgId = target.org_id;
    const blobId = target.blob_id || target.blob_alias;
    const sessionId = target.session_id;
    if ( !orgId || !blobId || !sessionId ) {
      return null;
    }
    return Navigation.workflowPath(orgId, blobId, "debugger", sessionId, executionId);
  }

  formatExecutionResult(execution) {
    if ( !execution.result ) {
      return <span className="text-muted">No result</span>;
    }
    if ( "success" === execution.status ) {
      const result = execution.result;
      if ( result.execution && result.execution.id ) {
        const executionId = result.execution.id;
        const link = this.debuggerLinkForExecution(executionId);
        if ( link ) {
          return <>Workflow execution: <a href={link}>{executionId}</a></>;
        }
        return <>Workflow execution: {executionId}</>;
      }
      return <>{JSON.stringify(result)}</>;
    }
    if ( typeof execution.result === "string" ) {
      return <span className="text-danger">{execution.result}</span>;
    }
    if ( execution.result.message ) {
      return <span className="text-danger">{execution.result.message}</span>;
    }
    return <span className="text-danger">{JSON.stringify(execution.result)}</span>;
  }

  render() {
    const columns = [
      {
        "field": "id",
        "headerName": "Execution ID",
        "filter": true,
        "flex": 4,
        "sortable": false,
      },
      {
        "field": "created_at",
        "headerName": "Created",
        "filter": false,
        "flex": 5,
        "sortable": true,
        "sort": "desc",
        cellRenderer: params => {
          return ( <> { Utils.formatCompactDateTime(params.value, this.props.timezone) } </> );
        }
      },
      {
        "field": "status",
        "headerName": "Status",
        "filter": true,
        "flex": 3,
        "sortable": true,
        cellRenderer: params => {
          return ( <Badge className={executionStatusBadgeClass(params.value)}>{params.value}</Badge> );
        }
      },
      {
        "field": "result",
        "headerName": "Result",
        "filter": false,
        "flex": 6,
        "sortable": false,
        "wrapText": true,
        "autoHeight": true,
        "cellStyle": { "wordBreak": "break-word", "lineHeight": "1.4" },
        cellRenderer: params => {
          return ( <small className="text-muted">{this.formatExecutionResult(params.data)}</small> );
        }
      }
    ];

    return (
      <>
        {( this.state.progress || this.state.error ) &&
          <Container>
            <Row>
              <Col>
                <Form.Group>
                  <Alert variant={this.state.progress ? "primary" : "warning"}>
                    {this.state.message}
                  </Alert>
                </Form.Group>
              </Col>
            </Row>
          </Container>
        }
        <Container className={this.state.details ? "container-wide" : ""}>
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  Executions
                  <span className="float-right">
                    {this.state.executions &&
                      <>
                        Total fetched: <strong>{this.state.executions.length}</strong>
                        {this.state.lastExecutionId &&
                          <> | More available</>
                        }
                      </>
                    }
                    {!this.state.details &&
                      <ButtonGroup size="sm" className="header-options">
                        &nbsp;&nbsp;
                        <Button variant="light" onClick={() => { this.setState({ details: true }) }}><GoInfo/></Button>
                      </ButtonGroup>
                    }
                  </span>
                </Card.Header>
                <Card.Body>
                  <Card.Text>
                    <div className="ag-theme-quartz">
                      <AgGridReact
                        rowData={this.state.executions}
                        columnDefs={columns}
                        domLayout='autoHeight'
                        pagination={false}
                      />
                    </div>
                  </Card.Text>
                  {!this.state.progress &&
                    <Card.Text>
                      {this.state.lastExecutionId &&
                        <Button variant="light" size="sm" className="mr-3" onClick={() => {
                          this.loadExecutions(true);
                        }}><MdOutlineExpandMore /> Load More Results</Button>
                      }
                      <Button variant="light" size="sm" onClick={() => {
                        this.loadExecutions(false);
                      }}><MdRefresh /> Refresh</Button>
                    </Card.Text>
                  }
                </Card.Body>
              </Card>
            </Col>
            <Col sm={4} className={`detail-panel-col ${this.state.details ? "" : "hidden"}`}>
              <Card>
                <Card.Header>
                  Schedule Details
                  <span className="float-right">
                    <ButtonGroup size="sm" className="header-options">
                      <Button variant="light" onClick={() => { this.setState({ details: false }) }}><GoX/></Button>
                    </ButtonGroup>
                  </span>
                </Card.Header>
                <Card.Body>
                  {this.state.schedule &&
                    <>
                      <Card.Text as="h6" className="d-flex justify-content-between align-items-center bh-alias-fit">
                        Properties
                        <Clipboard text={JSON.stringify(this.state.schedule, null, 2)} />
                      </Card.Text>
                      <hr />
                      <Card.Text>
                        <table className="properties">
                          <tbody>
                            <tr>
                              <td className="prop-name-column">Schedule ID</td>
                              <td className="prop-value-column"><code>{this.state.schedule.id}</code></td>
                            </tr>
                            {this.state.schedule.alias &&
                              <tr>
                                <td className="prop-name-column">Alias</td>
                                <td className="prop-value-column">
                                  <Alias value={this.state.schedule.alias} />
                                </td>
                              </tr>
                            }
                            <tr>
                              <td className="prop-name-column">State</td>
                              <td className="prop-value-column">
                                <Badge className={scheduleStateBadgeClass(this.state.schedule.state)}>
                                  {this.state.schedule.state}
                                </Badge>
                              </td>
                            </tr>
                            <tr>
                              <td className="prop-name-column">Enabled</td>
                              <td className="prop-value-column">
                                <span style={{color: this.state.schedule.enabled
                                  ? "var(--bh-status-enabled)" : "var(--bh-status-disabled)"}}>&#9679;</span>
                                {" "}{this.state.schedule.enabled ? "Yes" : "No"}
                              </td>
                            </tr>
                            <tr>
                              <td className="prop-name-column">Repeat</td>
                              <td className="prop-value-column">
                                {"recurring_cron" === this.state.schedule.repeat ? "Recurring" : "One-time"}
                              </td>
                            </tr>
                            {"recurring_cron" === this.state.schedule.repeat &&
                              <tr>
                                <td className="prop-name-column">Cron</td>
                                <td className="prop-value-column"><code>{this.state.schedule.cron_expression}</code></td>
                              </tr>
                            }
                            {"one_time" === this.state.schedule.repeat &&
                              <tr>
                                <td className="prop-name-column">Time</td>
                                <td className="prop-value-column">
                                  {Utils.formatNaiveDateTime(this.state.schedule.invocation_time)}
                                </td>
                              </tr>
                            }
                            <tr>
                              <td className="prop-name-column">Timezone</td>
                              <td className="prop-value-column">{this.state.schedule.timezone}</td>
                            </tr>
                            {this.state.schedule.start_date &&
                              <tr>
                                <td className="prop-name-column">Start Date</td>
                                <td className="prop-value-column">
                                  {Utils.formatDateTime(this.state.schedule.start_date, this.props.timezone)}
                                </td>
                              </tr>
                            }
                            {this.state.schedule.end_date &&
                              <tr>
                                <td className="prop-name-column">End Date</td>
                                <td className="prop-value-column">
                                  {Utils.formatDateTime(this.state.schedule.end_date, this.props.timezone)}
                                </td>
                              </tr>
                            }
                            <tr>
                              <td className="prop-name-column">Created</td>
                              <td className="prop-value-column">
                                {Utils.formatDateTime(this.state.schedule.created_at, this.props.timezone)}
                              </td>
                            </tr>
                            <tr>
                              <td className="prop-name-column"></td>
                              <td className="prop-value-column">
                                {Utils.formatTimeAgo(this.state.schedule.created_at)}
                              </td>
                            </tr>
                            {this.state.schedule.modified_at &&
                              <tr>
                                <td className="prop-name-column">Modified</td>
                                <td className="prop-value-column">
                                  {Utils.formatDateTime(this.state.schedule.modified_at, this.props.timezone)}
                                </td>
                              </tr>
                            }
                            {this.state.schedule.modified_at &&
                              <tr>
                                <td className="prop-name-column"></td>
                                <td className="prop-value-column">
                                  {Utils.formatTimeAgo(this.state.schedule.modified_at)}
                                </td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </Card.Text>

                      <Card.Text as="h6">Workflow Target</Card.Text>
                      <hr />
                      <Card.Text>
                        <table className="properties">
                          <tbody>
                            <tr>
                              <td className="prop-name-column">Org / Blob</td>
                              <td className="prop-value-column">
                                <code>{this.formatTarget(this.state.schedule)}</code>
                              </td>
                            </tr>
                            {this.state.schedule.invocation_target &&
                              <>
                                <tr>
                                  <td className="prop-name-column">Revision</td>
                                  <td className="prop-value-column">
                                    <code>{this.state.schedule.invocation_target.revision_id}</code>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="prop-name-column">Session</td>
                                  <td className="prop-value-column">
                                    <code>{this.state.schedule.invocation_target.session_id}</code>
                                  </td>
                                </tr>
                                <tr>
                                  <td className="prop-name-column">Workflow</td>
                                  <td className="prop-value-column">
                                    <Alias value={this.state.schedule.invocation_target.workflow_alias} />
                                  </td>
                                </tr>
                                {this.state.schedule.invocation_target.description &&
                                  <tr>
                                    <td className="prop-name-column">Description</td>
                                    <td className="prop-value-column">
                                      {this.state.schedule.invocation_target.description}
                                    </td>
                                  </tr>
                                }
                                <tr>
                                  <td className="prop-name-column">Input Data</td>
                                  <td className="prop-value-column">
                                    {this.renderInputData(this.state.schedule.invocation_target.input_data)}
                                  </td>
                                </tr>
                              </>
                            }
                          </tbody>
                        </table>
                      </Card.Text>
                    </>
                  }

                  {(!this.state.progress && this.props.canEditRevision ) &&
                    <>
                      <Card.Text as="h6">Actions</Card.Text>
                      <ActionCards>
                        <ActionCard helper={"completed" === (this.state.schedule && this.state.schedule.state)
                          ? "Schedule is completed"
                          : "Opens the schedule's settings for editing"}>
                          <Button variant="success" size="sm"
                            disabled={"completed" === (this.state.schedule && this.state.schedule.state)}
                            onClick={() => {
                              this.setState({ editScheduleModalShow: true });
                            }}><GoPencil /> Edit</Button>
                        </ActionCard>

                        <ActionCard helper="Deletes the schedule and its executions">
                          <Button variant="danger" size="sm" onClick={() => {
                            this.setState({ deleteConfirmModalShow: true });
                          }}><GoTrash /> Delete</Button>
                        </ActionCard>
                      </ActionCards>
                    </>
                  }
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {this.state.schedule &&
          <ScheduleModal
            schedule={this.state.schedule}
            show={this.state.editScheduleModalShow}
            onHide={() => this.setState({ editScheduleModalShow: false })}
            handleSubmit={(schedule) => this.updateSchedule(schedule)}
          />
        }

        {this.state.schedule &&
          <ConfirmModal
            show={this.state.deleteConfirmModalShow}
            onHide={() => this.setState({ deleteConfirmModalShow: false })}
            onConfirm={() => this.deleteSchedule()}
            title="Delete Schedule"
            body={
              <>
                Are you sure that you would like to delete schedule{" "}
                {this.state.schedule.alias
                  ? <Alias value={this.state.schedule.alias} />
                  : <strong>{this.state.schedule.id}</strong>}? This
                removes the schedule and all of its executions; it cannot be undone.
              </>
            }
          />
        }
      </>
    );
  }
}
